import getLanguage from './getLanguage';

let scope = window;

// For testing purposes
const changeScope = (newScope) => {
  scope = newScope;
  return scope;
};

const supportsDateFormat = () =>
  scope.Intl && typeof scope.Intl === 'object' && typeof scope.Intl.DateTimeFormat === 'function';

const longDateFormattersByLang = new Map();
const mediumDateFormattersByLang = new Map();
const shortDateFormattersByLang = new Map();

const createDateObject = (input) => {
  if (input == null) {
    return null;
  }

  if (typeof input === 'string' || input instanceof String) {
    const splitedDate = input.split('-');
    const date = new Date(
      parseInt(splitedDate[0], 10),
      parseInt(splitedDate[1], 10) - 1,
      parseInt(splitedDate[2], 10)
    );

    if (input.length !== 10 || Number.isNaN(new Date(input).getTime())) {
      throw new Error(`Failed to parse string as a date: ${input}`);
    }
    return date;
  }

  if (!(input instanceof Date)) {
    throw new Error('Non-date passed to date formatter.');
  }

  return input;
};

const getLongDateFormatter = (lang) => {
  let formatter = longDateFormattersByLang.get(lang);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(lang, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    longDateFormattersByLang.set(lang, formatter);
  }

  return formatter;
};

const getMediumDateFormatter = (lang) => {
  let formatter = mediumDateFormattersByLang.get(lang);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(lang, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
    mediumDateFormattersByLang.set(lang, formatter);
  }

  return formatter;
};

const getShortDateFormatter = (lang) => {
  let formatter = shortDateFormattersByLang.get(lang);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(lang, {
      month: 'numeric',
      day: 'numeric',
    });
    shortDateFormattersByLang.set(lang, formatter);
  }

  return formatter;
};

const formatLongDate = (dateParam) => {
  const date = createDateObject(dateParam);

  if (date === null) {
    return null;
  }

  const lang = getLanguage();
  if (lang && supportsDateFormat()) {
    const formatter = getLongDateFormatter(lang);
    return formatter.format(date);
  }

  return [date.getFullYear(), date.getMonth() + 1, date.getDate()].join('-');
};

const formatMediumDate = (dateParam) => {
  const date = createDateObject(dateParam);

  if (date === null) {
    return null;
  }

  const lang = getLanguage();
  if (lang && supportsDateFormat()) {
    const formatter = getMediumDateFormatter(lang);
    return formatter.format(date);
  }

  return [String(date.getFullYear()).substr(2), date.getMonth() + 1, date.getDate()].join('-');
};

const formatShortDate = (dateParam) => {
  const date = createDateObject(dateParam);

  if (date === null) {
    return null;
  }

  const lang = getLanguage();
  if (lang && supportsDateFormat()) {
    const formatter = getShortDateFormatter(lang);
    return formatter.format(date);
  }

  return [date.getMonth() + 1, date.getDate()].join('-');
};

const formatIsoDate = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 101).slice(-2),
    String(date.getDate() + 100).slice(-2),
  ].join('-');

/**
 * @param {string} time - hh:mm:ss.
 */
const formatIsoDateTime = (date, time) => {
  const dateObject = createDateObject(date);
  const offsetMinutes = dateObject.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMinutesRemainder = Math.abs(offsetMinutes) % 60;

  const offsetSign = offsetMinutes < 0 ? '+' : '-';

  const paddedOffsetHours = offsetHours.toString().padStart(2, '0');
  const paddedOffsetMinutes = offsetMinutesRemainder.toString().padStart(2, '0');

  const offsetString = offsetSign + paddedOffsetHours + ':' + paddedOffsetMinutes;
  const formattedString = `${date}T${time}${offsetString}`;

  return formattedString;
};

const isLeapYear = (year) => {
  return (
    (year % 4 === 0 && year % 100 !== 0 && year % 400 !== 0) ||
    (year % 100 === 0 && year % 400 === 0)
  );
};

function isToday(date) {
  return formatIsoDate(date) === formatIsoDate(new Date());
}

function addDays(date, days) {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  // Correct hour if timezone changed
  newDate.setHours(
    newDate.getHours() + (date.getTimezoneOffset() - newDate.getTimezoneOffset()) / 60
  );
  return newDate;
}

function subDays(date, days) {
  return addDays(date, -1 * days);
}

function dateTimeToDate(dateTime) {
  return new Date(formatIsoDate(dateTime));
}

function isTomorrow(date) {
  return formatIsoDate(date) === formatIsoDate(addDays(new Date(), 1));
}

function isYesterday(date) {
  return formatIsoDate(date) === formatIsoDate(addDays(new Date(), -1));
}

function isPast(date) {
  return new Date(formatIsoDate(date)) < new Date(formatIsoDate(new Date()));
}

function isFuture(date) {
  return new Date(formatIsoDate(date)) > new Date(formatIsoDate(new Date()));
}

function calculateAge(dateParam) {
  const birthDate = createDateObject(dateParam);

  if (birthDate === null) {
    return null;
  }

  const today = new Date();
  const birthYear = birthDate.getFullYear();
  const birthMonth = birthDate.getMonth();
  const birthDay = birthDate.getDate();

  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const currentDay = today.getDate();

  let age = currentYear - birthYear;

  // Check if birthday hasn't occurred this year yet
  if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
    age--;
  }

  return age;
}

export {
  createDateObject,
  formatLongDate,
  formatMediumDate,
  formatShortDate,
  formatIsoDate,
  formatIsoDateTime,
  changeScope as changeScopeOnlyForTesting,
  addDays,
  isLeapYear,
  getLanguage,
  isToday,
  isTomorrow,
  isYesterday,
  isPast,
  isFuture,
  subDays,
  dateTimeToDate,
  calculateAge,
};
